import {AfterViewInit, Component, computed, inject, OnDestroy, signal, Signal, ViewChild} from '@angular/core';
import {SystemEventService} from '../../../system-event/services/system-event.service';
import {GroupedSystemEvents} from '../../../system-event/classes/grouped-system-events';
import {FormControl, FormGroup} from '@angular/forms';
import {ReportService} from '../../services/report.service';
import {Report} from '../../classes/report';
import {State} from '../../../outage/classes/state';
import moment from 'moment';
import {MatDrawer} from '@angular/material/sidenav';
import {SitRepService} from '../../services/sit-rep.service';
import {SystemEvent} from '../../../system-event/classes/system-event';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {ElementOptionsConfig} from '../../classes/element-option';
import {GenOutageAggregationLevel} from 'frontend/generated/serverModels/GenOutageAggregationLevel';
import {ApplicationConfig} from '../../../../classes/application-config';
import {debounceTime, pairwise, startWith, take} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {SitRepExportModalComponent} from '../../modals/sit-rep-export-modal/sit-rep-export-modal.component';
import {CardFilters} from '../../../../../shared/classes/card-filters';
import {Subject} from 'rxjs';

interface FilterGroupProps {
    event: SystemEvent;
    locations: State[];
    start: Date;
    end: Date;
}

@Component({
    selector: 'eaglei-sit-rep-data-report',
    templateUrl: './sit-rep-data-report.component.html',
    styleUrls: ['../reports.scss', './sit-rep-data-report.component.scss'],
    standalone: false,
})
export class SitRepDataReportComponent extends Report implements AfterViewInit, OnDestroy {
    @ViewChild(MatDrawer) configPanel: MatDrawer;

    // Services
    private systemEventService = inject(SystemEventService);
    private reportService = inject(ReportService);
    private sitRepService = inject(SitRepService);
    private dialog = inject(MatDialog);

    // Filters
    protected filterGroup = new FormGroup({
        event: new FormControl<SystemEvent>(null),
        locations: new FormControl<State[]>([]),
        start: new FormControl<Date>(null),
        end: new FormControl<Date>(null),
    });

    // UI
    protected minDate = signal<moment.Moment>(ApplicationConfig.roundMinute().subtract(30, 'days'));
    protected maxDate = signal<moment.Moment>(ApplicationConfig.roundMinute());

    private eventSignal: Signal<SystemEvent> = toSignal(this.filterGroup.controls.event.valueChanges);

    protected startDate: Signal<moment.Moment> = computed(() => {
        return this.eventSignal()?.eventStart;
    });

    protected endDate: Signal<moment.Moment> = computed(() => {
        if (this.eventSignal()?.eventEnd.isSameOrAfter(ApplicationConfig.roundMinute())) {
            return ApplicationConfig.roundMinute();
        }

        return this.eventSignal()?.eventEnd;
    });

    protected locations: Signal<State[]> = computed(() => {
        return this.eventSignal()?.states;
    });

    public openConfigSideBar: Signal<boolean> = computed(() => {
        const currentOptions = this.sitRepService.openedConfigOptions;
        return currentOptions()?.options?.length > 0;
    });
    public activeConfigOptions: Signal<ElementOptionsConfig> = this.sitRepService.openedConfigOptions;

    protected showMask: boolean = true;

    protected readonly aggregationLevels = GenOutageAggregationLevel;

    protected events: Signal<GroupedSystemEvents> = toSignal(this.systemEventService.getGroupedEvents());

    constructor() {
        super();

        this.reportService
            .getReportData()
            .pipe(takeUntilDestroyed())
            .subscribe((r) => this.initializeReportInfo(r));

        // There may be a better way to do this, but there was an issue that when the event updated, it was not passing the
        // correct dates to the filters because it was always using the date range date. The pairwise was the best way I
        // could find to keep track of the previous values
        this.filterGroup.valueChanges
            .pipe(debounceTime(100), startWith(this.filterGroup.value), pairwise(), takeUntilDestroyed())
            .subscribe(([prev, cur]: [FilterGroupProps, FilterGroupProps]) => {
                this.showMask = false;

                // If there was a change in the event, we want to use all the event data.
                if (prev.event?.id !== cur.event?.id) {
                    this.minDate.set(cur.event.eventStart);
                    this.maxDate.set(cur.event.eventEnd);
                    this.sitRepService.updateFilters({
                        event: cur.event,
                        locations: cur.event.states,
                        startDate: cur.event.eventStart,
                        endDate: cur.event.eventEnd,
                    });
                    return;
                }

                // If the dates changed, we only want to update the dates
                if (prev.start?.getTime() !== cur.start?.getTime() || prev.end?.getTime() !== cur.end?.getTime()) {
                    this.sitRepService.updateFilters({
                        startDate: ApplicationConfig.roundMinute(moment(cur.start)),
                        endDate: ApplicationConfig.roundMinute(moment(cur.end)),
                    });
                    return;
                }

                // If the location changes, we only want to update the locations
                const currentLocations = cur.locations.map((s) => s.id);
                const prevLocations = prev.locations?.map((s) => s.id) || [];

                if (currentLocations.every((currentStateId) => prevLocations.includes(currentStateId))) {
                    this.sitRepService.updateFilters({
                        locations: cur.locations,
                    });
                    return;
                }
            });
    }

    public ngAfterViewInit(): void {
        this.systemEventService
            .getMostRecentEvent()
            .pipe(take(1))
            .subscribe((event) => {
                const endDate = event.eventEnd.isSameOrAfter(ApplicationConfig.roundMinute())
                    ? ApplicationConfig.roundMinute()
                    : event.eventEnd;
                this.filterGroup.patchValue({
                    event,
                    start: ApplicationConfig.roundMinute(event.eventStart).toDate(),
                    end: endDate.toDate(),
                    locations: event.states,
                });
            });
    }

    protected updateLocations(states: State[]): void {
        this.filterGroup.patchValue({locations: states});
    }

    protected updateDateRange(dates: CardFilters): void {
        this.filterGroup.patchValue({
            start: dates.startDate.toDate(),
            end: dates.endDate.toDate(),
        });
    }

    public applyConfig(): void {
        this.sitRepService.closeConfigSideBar(true);
    }

    public cancelConfig(): void {
        this.sitRepService.closeConfigSideBar(false);
    }

    public openExportModal() {
        this.dialog.open(SitRepExportModalComponent, {width: `500px`});
    }
}
